import { useState } from 'react';
import {
  MapPin,
  Phone,
  Calendar,
  Building,
  FileText,
  User,
  Clock,
  Sparkles,
  UserCheck,
} from 'lucide-react';
import { Modal } from '../common/Modal';
import { Badge, getCuisineBadgeVariant } from '../common/Badge';
import { Button } from '../common/Button';
import { useUpdateLead, useLeadActivities } from '../../hooks/useLeads';
import { useRole } from '../../contexts/RoleContext';
import type { Lead, LeadActivity } from '../../api/client';

interface LeadModalProps {
  lead: Lead | null;
  isOpen: boolean;
  onClose: () => void;
}

const statusOptions = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'closed', label: 'Closed' },
];

export function LeadModal({ lead, isOpen, onClose }: LeadModalProps) {
  const [notes, setNotes] = useState(lead?.notes || '');
  const [selectedStatus, setSelectedStatus] = useState(lead?.status || 'new');

  const updateLead = useUpdateLead();
  const { data: activitiesData } = useLeadActivities(lead?.id || 0);
  const { can } = useRole();

  const canUpdateStatus = can('leads:update_status');
  const canSeeAssignments = can('leads:view_assignments');

  if (!lead) return null;

  const handleSave = async () => {
    const updates: Partial<Lead> = {};

    if (selectedStatus !== lead.status) {
      updates.status = selectedStatus;
    }

    if (notes !== lead.notes) {
      updates.notes = notes;
    }

    if (Object.keys(updates).length > 0) {
      await updateLead.mutateAsync({ id: lead.id, data: updates });
    }

    onClose();
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={lead.name} size="lg">
      <div className="p-6 space-y-6">
        {/* Badges */}
        <div className="flex flex-wrap gap-2">
          {lead.is_new && (
            <Badge variant="new" className="flex items-center space-x-1">
              <Sparkles className="w-3 h-3" />
              <span>New Lead</span>
            </Badge>
          )}
          {lead.cuisine_detected && (
            <Badge variant={getCuisineBadgeVariant(lead.cuisine_detected)}>
              {lead.cuisine_detected}
            </Badge>
          )}
          {lead.source_name && (
            <Badge>{lead.source_name}</Badge>
          )}
        </div>

        {/* Details grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left column - Contact info */}
          <div className="space-y-4">
            <h4 className="font-semibold text-font-primary">Contact Information</h4>

            {lead.address && (
              <div className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-font-secondary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-font-primary">{lead.address}</p>
                  <p className="text-font-secondary text-sm">
                    {[lead.city, lead.state, lead.zipcode].filter(Boolean).join(', ')}
                  </p>
                </div>
              </div>
            )}

            {lead.phone && (
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-font-secondary" />
                <a
                  href={`tel:${lead.phone}`}
                  className="text-brand-primary hover:underline"
                >
                  {lead.phone}
                </a>
              </div>
            )}

            {lead.region && (
              <div className="flex items-center space-x-3">
                <Building className="w-5 h-5 text-font-secondary" />
                <span className="text-font-primary">{lead.region}</span>
              </div>
            )}
          </div>

          {/* Right column - License info */}
          <div className="space-y-4">
            <h4 className="font-semibold text-font-primary">License Information</h4>

            {lead.license_type && (
              <div className="flex items-start space-x-3">
                <FileText className="w-5 h-5 text-font-secondary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-font-primary">{lead.license_type}</p>
                  {lead.license_status && (
                    <p className="text-font-secondary text-sm">{lead.license_status}</p>
                  )}
                </div>
              </div>
            )}

            {lead.first_seen_at && (
              <div className="flex items-center space-x-3">
                <Calendar className="w-5 h-5 text-font-secondary" />
                <span className="text-font-primary">First seen: {formatDate(lead.first_seen_at)}</span>
              </div>
            )}

            {lead.effective_date && (
              <div className="flex items-center space-x-3">
                <Clock className="w-5 h-5 text-font-secondary" />
                <span className="text-font-primary">Effective: {formatDate(lead.effective_date)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Assigned To (read-only display) - only visible to users with claim permission */}
        {canSeeAssignments && lead.assigned_to && (
          <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
            <UserCheck className="w-5 h-5 text-green-600" />
            <div>
              <span className="text-sm text-font-secondary">Assigned to: </span>
              <span className="font-medium text-font-primary">{lead.assigned_to}</span>
            </div>
          </div>
        )}

        {/* Status */}
        <div className="space-y-2">
          <label className="block font-semibold text-font-primary">Status</label>
          {canUpdateStatus ? (
            <div className="flex flex-wrap gap-2">
              {statusOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setSelectedStatus(option.value as Lead['status'])}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedStatus === option.value
                      ? 'bg-brand-primary text-white'
                      : 'bg-white text-font-secondary border border-card-border hover:border-brand-primary'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          ) : (
            <Badge variant="status" statusType={lead.status}>
              {lead.status.charAt(0).toUpperCase() + lead.status.slice(1)}
            </Badge>
          )}
        </div>

        {/* Notes */}
        <div className="space-y-2">
          <label className="block font-semibold text-font-primary">Notes</label>
          {canUpdateStatus ? (
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="input min-h-[100px] resize-y"
              placeholder="Add notes about this lead..."
            />
          ) : (
            <div className="p-3 bg-gray-50 rounded-lg min-h-[100px] text-font-secondary">
              {lead.notes || 'No notes added.'}
            </div>
          )}
        </div>

        {/* Activity log - only visible to users with claim permission */}
        {canSeeAssignments && activitiesData?.activities && activitiesData.activities.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-semibold text-font-primary">Recent Activity</h4>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {activitiesData.activities.slice(0, 5).map((activity: LeadActivity) => (
                <div
                  key={activity.id}
                  className="flex items-center space-x-3 text-sm text-font-secondary"
                >
                  <User className="w-4 h-4" />
                  <span>
                    {activity.user_email} - {activity.action}
                    {activity.new_value && `: ${activity.new_value}`}
                  </span>
                  <span className="text-xs">
                    {new Date(activity.created_at).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-card-border">
          <Button variant="outline" onClick={onClose}>
            {canUpdateStatus ? 'Cancel' : 'Close'}
          </Button>
          {canUpdateStatus && (
            <Button
              variant="primary"
              onClick={handleSave}
              isLoading={updateLead.isPending}
            >
              Save Changes
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
